import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { distinctUntilChanged, interval, map, Subject, takeUntil } from "rxjs";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from "@fuse/components/navigation";
import { Navigation } from "app/core/navigation/navigation.types";
import { NavigationService } from "app/core/navigation/navigation.service";
import { User } from "app/core/user/user.types";
import { UserService } from "app/core/user/user.service";
import { FetchData } from "app/shared/generalMethods";

@Component({
  selector: "classy-layout",
  templateUrl: "./classy.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  navigation: Navigation;
  user: User;
  userRecord: any;
  name = "";
  membershipId: string;
  formattedParentImage: string;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _navigationService: NavigationService,
    private _userService: UserService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private _fetchData: FetchData
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
      });

    // Subscribe to the user service
    this._userService.user$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((user: User) => {
        this.user = user;
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes("md");
      });

    this.loadUserRecord();
    interval(3000)
      .pipe(
        takeUntil(this._unsubscribeAll),
        map(() => JSON.stringify(this._fetchData.data.lstParents?.[0] || {})),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.loadUserRecord();
      });
  }

  /**
   * Load user record data
   */
  formatSalesforceImageUrl(imageUrl: string): string {
    if (!imageUrl) return null;

    // Remove <p> tags and replace &quot; with empty string
    return imageUrl.replace(/<\/?p>/g, '"').replace(/&quot;/g, "");
  }

  /**
   * Load user record data
   */
  loadUserRecord(): void {
    const newUserRecord = this._fetchData.data.lstParents?.[0];
    if (!newUserRecord) return;

    this.userRecord = newUserRecord;

    // ✅ Set name
    this.name = newUserRecord.Name || "";

    // ✅ Format image
    if (newUserRecord.Parent_Image__c) {
      const formattedImage = this.formatSalesforceImageUrl(
        newUserRecord.Parent_Image__c
      );

      // 🧠 Skip default placeholder image
      if (
        formattedImage &&
        !formattedImage.includes("assets/images/avatars/user.png")
      ) {
        this.formattedParentImage = formattedImage;
      } else {
        this.formattedParentImage = null; // or leave undefined
      }
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  /**
   * Navigate to all-membership page
   */
  navigateToAllMembership(): void {
    this._router.navigate(["/manage-profile"]);
  }

  /**
   * Sign out
   */
  signOut(): void {
    localStorage.clear();
    localStorage.removeItem("user-loggedin");
    this._router.navigate(["/sign-out"]);
  }
}
